import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { offboardingAPI } from '../lib/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/use-toast';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { OffboardingRequest } from '../types';
import { CheckCircle, XCircle, AlertTriangle, User } from 'lucide-react';

type ConfirmationStatus = 'deactivated' | 'still_active' | 'not_applicable';

interface AppConfirmation {
  appId: string;
  appName: string;
  status: ConfirmationStatus | null;
}

export const VerificationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [offboarding, setOffboarding] = useState<OffboardingRequest | null>(null);
  const [myApps, setMyApps] = useState<AppConfirmation[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (id && user?.email) {
      loadOffboarding();
    }
  }, [id, user]);

  const loadOffboarding = async () => {
    if (!id || !user?.email) return;

    const userEmail = user.email;

    try {
      setLoading(true);
      const data = await offboardingAPI.getById(id);

      if (!data) {
        setLoading(false);
        return;
      }

      setOffboarding(data);

      // Filter applications where I'm an admin
      const myApplications: AppConfirmation[] = [];
      data.applications.forEach((app) => {
        if (app.adminEmails.includes(userEmail)) {
          const currentStatus = app.confirmationStatus[userEmail];
          myApplications.push({
            appId: app.appId,
            appName: app.appName,
            status: currentStatus?.status === 'pending' ? null : (currentStatus?.status as ConfirmationStatus || null),
          });
        }
      });

      setMyApps(myApplications);
    } catch (error) {
      console.error('Error loading offboarding:', error);
      toast({
        title: 'Error',
        description: 'Failed to load offboarding request',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (appId: string, status: ConfirmationStatus) => {
    setMyApps((prev) =>
      prev.map((app) =>
        app.appId === appId ? { ...app, status } : app
      )
    );
  };

  const handleSubmit = async () => {
    if (!offboarding || !user?.email) return;

    const userEmail = user.email;

    // Check if all apps have a status
    const allConfirmed = myApps.every((app) => app.status !== null);
    if (!allConfirmed) {
      toast({
        title: 'Incomplete Form',
        description: 'Please confirm the status for all applications',
        className: 'bg-yellow-50 border-yellow-200',
      });
      return;
    }

    try {
      setSubmitting(true);

      // Submit confirmations via Cloud Function for proper authorization
      const functions = getFunctions();
      const updateConfirmation = httpsCallable(functions, 'updateOffboardingConfirmation');
      await updateConfirmation({
        offboardingId: offboarding.id,
        confirmations: myApps.map((app) => ({
          appId: app.appId,
          status: app.status!,
        })),
      });

      toast({
        title: 'Success',
        description: 'Your confirmations have been submitted',
        className: 'bg-green-50 border-green-200',
      });

      // Navigate to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error) {
      console.error('Error submitting confirmations:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit confirmations',
        className: 'bg-red-50 border-red-200',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 flex items-center justify-center">
        <div className="text-muted-foreground">Loading verification request...</div>
      </div>
    );
  }

  if (!offboarding || myApps.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30">
            <CardHeader>
              <CardTitle className="text-red-800 dark:text-red-200">No Access Required</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-red-700 dark:text-red-300 mb-4">
                You don't have any applications to verify for this offboarding request.
              </p>
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const allConfirmed = myApps.every((app) => app.status !== null);
  const confirmedCount = myApps.filter((app) => app.status !== null).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header Card */}
        <Card className="mb-6 border-indigo-200 dark:border-indigo-800 bg-gradient-to-r from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-900/30">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-2xl mb-2">User Access Verification</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Please confirm the deactivation status for <strong>{offboarding.employeeName}</strong>
                </p>
              </div>
              <Badge variant="outline" className="text-indigo-600 dark:text-indigo-400 border-indigo-600 dark:border-indigo-400">
                {confirmedCount} / {myApps.length} Confirmed
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 text-sm bg-white/50 dark:bg-white/5 rounded-lg p-3 border">
              <User className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              <div>
                <span className="font-medium">Employee:</span> {offboarding.employeeName}
              </div>
              <span>•</span>
              <div>
                <span className="font-medium">Email:</span> {offboarding.employeeEmail}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2 text-sm text-muted-foreground">
            <span>Verification Progress</span>
            <span>{Math.round((confirmedCount / myApps.length) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-indigo-600 dark:bg-indigo-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(confirmedCount / myApps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Applications List */}
        <div className="space-y-4">
          {myApps.map((app) => (
            <Card
              key={app.appId}
              className={`transition-all ${
                app.status
                  ? 'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20'
                  : 'border-orange-200 dark:border-orange-800 bg-orange-50/30 dark:bg-orange-950/20'
              }`}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{app.appName}</CardTitle>
                  {app.status && (
                    <Badge className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900/30">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Confirmed
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <Label className="block mb-3 text-sm font-medium">
                  Has this user been deactivated from <strong>{app.appName}</strong>?
                </Label>

                <div className="space-y-3">
                  {/* Deactivated */}
                  <label
                    className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                      app.status === 'deactivated'
                        ? 'border-green-500 bg-green-50 dark:bg-green-950/30 ring-2 ring-green-500'
                        : 'border-gray-200 dark:border-gray-700 bg-card hover:border-green-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`status-${app.appId}`}
                      value="deactivated"
                      checked={app.status === 'deactivated'}
                      onChange={() => handleStatusChange(app.appId, 'deactivated')}
                      className="w-4 h-4 text-green-600 focus:ring-green-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 font-medium text-green-700 dark:text-green-300">
                        <CheckCircle className="h-4 w-4" />
                        Yes, deactivated
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        User no longer has access to this application
                      </p>
                    </div>
                  </label>

                  {/* Still Active */}
                  <label
                    className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                      app.status === 'still_active'
                        ? 'border-red-500 bg-red-50 dark:bg-red-950/30 ring-2 ring-red-500'
                        : 'border-gray-200 dark:border-gray-700 bg-card hover:border-red-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`status-${app.appId}`}
                      value="still_active"
                      checked={app.status === 'still_active'}
                      onChange={() => handleStatusChange(app.appId, 'still_active')}
                      className="w-4 h-4 text-red-600 focus:ring-red-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 font-medium text-red-700 dark:text-red-300">
                        <XCircle className="h-4 w-4" />
                        No, still active
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        User still has access and needs to be deactivated
                      </p>
                    </div>
                  </label>

                  {/* Not Applicable */}
                  <label
                    className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                      app.status === 'not_applicable'
                        ? 'border-gray-500 bg-muted/50 ring-2 ring-gray-500'
                        : 'border-gray-200 dark:border-gray-700 bg-card hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`status-${app.appId}`}
                      value="not_applicable"
                      checked={app.status === 'not_applicable'}
                      onChange={() => handleStatusChange(app.appId, 'not_applicable')}
                      className="w-4 h-4 text-gray-600 focus:ring-gray-500"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 font-medium text-gray-700 dark:text-gray-300">
                        <AlertTriangle className="h-4 w-4" />
                        Not applicable
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        User never had access to this application
                      </p>
                    </div>
                  </label>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Submit Button */}
        <div className="mt-8 flex gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="flex-1"
          >
            Save for Later
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!allConfirmed || submitting}
            className="flex-1 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800"
          >
            {submitting ? (
              'Submitting...'
            ) : allConfirmed ? (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Submit All Confirmations
              </>
            ) : (
              `Confirm ${myApps.length - confirmedCount} More`
            )}
          </Button>
        </div>

        {/* Info Box */}
        {!allConfirmed && (
          <Card className="mt-6 border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/30">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium">
                    Please confirm all applications before submitting
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                    You have {myApps.length - confirmedCount} application{myApps.length - confirmedCount !== 1 ? 's' : ''} pending confirmation
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
